import { compose, flatten, pluck, pathOr, or, equals, over, lensPath, ifElse, isNil, append, insert, prop, assoc, dissoc, defaultTo, remove, adjust, lt, length, path, update, map, reject, filter, includes, assocPath, isNotEmpty, hasPath, identity, propOr, isNotNil, find, propEq } from 'ramda';
import { isNilOrEmpty, isTrue, notEqual } from 'ramda-adjunct';
import { BASE_PADDING_DOCUMENT } from '../_CONST';
import { ACTIONS, TARGET_TYPES, buildEntry, buildWidgetPlacements, firstRowWidgetId } from '../lib/sessionHistory';
import { countWidgetOccurrences } from '../lib/sessionBaseline';

export const useWidgets = (state, setState, getConditionsAreValid, pushEvent, widgets, markStructuralChange) => {
  // Forward the metadata argument only when there is one: entries that carry no
  // metadata must keep calling pushEvent with a single argument.
  const emit = (entry, meta) => {
    if (!pushEvent) return;
    if (isNil(meta)) {
      pushEvent(entry);
      return;
    }
    pushEvent(entry, meta);
  };
  const notifyStructuralChange = () => {
    if (!markStructuralChange) return;
    markStructuralChange();
  };
  // targetName is required on every history EVENT entry (documentHistories schema).
  // Sections have a derived label (back-end fallback), but widgets have a real name
  // only known here on the front — resolve it from the loaded widgets list.
  const getWidgetName = (widgetId) => propOr(null, 'name', find(propEq(widgetId, 'id'), widgets || []));

  // Placements must be captured on the pre-mutation pages: the back-end otherwise
  // rebuilds them from the saved (post-deletion) pages, where the widget is gone,
  // and the entry ends up with no origin placement
  const placementsBeforeMutation = (widgetId, sectionIndex, type) =>
    buildWidgetPlacements(pathOr([], ['values', 'pages'], state), widgetId, sectionIndex, type);

  // How many occurrences of the widget remain in that section and block once the
  // removal is applied. Compared with the session baseline count, this tells a
  // genuine deletion from the removal of an occurrence added in-session, without
  // relying on row or column indexes that shift during the session.
  const occurrencesAfterRemoval = (widgetId, sectionIndex, type) =>
    countWidgetOccurrences(pathOr([], ['values', 'pages'], state), widgetId, sectionIndex, type) - 1;

  const widgetsAreDefined = compose(
    isNotEmpty,
    flatten,
    pluck('widgets'),
    pathOr([], ['values', 'pages'])
  )(state);

  const isHeaderOrFooter = (type) => or(equals('header')(type), equals('footer')(type));
  const pathWidgetsSection = (sectionIndex, type) => ['values', 'pages', sectionIndex, type];
  const pathWidgetsRow = (sectionIndex, rowIndex, type) => [...pathWidgetsSection(sectionIndex, type), rowIndex, 'widgets'];
  const pathDataRules = (sectionIndex, rowIndex, widgetIndex, type) => [...pathWidgetsRow(sectionIndex, rowIndex, type), widgetIndex, 'rules'];

  const addWidget = (sectionIndex, type) => (rowIndex) => {
    // Inserting a row shifts the rowIndex of every row already below it, so the
    // history entries already on the stack can no longer be compared against the
    // next one on their rowIndex/widgetIndex.
    notifyStructuralChange();
    return setState(over(
      lensPath(pathWidgetsSection(sectionIndex, type)),
      (data) => {
        const widgetData = {
          widgets: [{ rules: [] }],
          padding: BASE_PADDING_DOCUMENT
        };
        if (!isHeaderOrFooter(type)) widgetData.breakPage = false;
        return ifElse(
          isNil,
          () => append(widgetData)(data),
          () => insert(rowIndex, widgetData)(data)
        )(rowIndex);
      }
    ));
  };

  const addRowWidget = (sectionIndex, type) => rowIndex => setState(over(
    lensPath(pathWidgetsRow(sectionIndex, rowIndex, type)),
    append({ rules: [] })
  ));

  const updateManualBreakPageState = (sectionIndex, rowIndex, checked) => (value) => {
    const path = ['values', 'pages', sectionIndex, 'widgets', rowIndex];
    return ifElse(
      hasPath(path),
      over(
        lensPath(path),
        compose(
          ifElse(
            compose(isTrue, prop('breakPage')),
            assoc('rulesBreakPage', []),
            dissoc('rulesBreakPage')
          ),
          assoc('breakPage', checked)
        )
      ),
      identity
    )(value);
  };

  const updateManualBreakPage = (sectionIndex) => (rowIndex, checked) =>
    setState(updateManualBreakPageState(sectionIndex, rowIndex, checked));

  const addManualBreakPageRule = (sectionIndex) => (rowIndex) => setState(over(
    lensPath(['values', 'pages', sectionIndex, 'widgets', rowIndex, 'rulesBreakPage']),
    compose(
      append({}),
      defaultTo([])
    )
  ));

  const removeManualBreakPageRule = (sectionIndex) => (rowIndex) => (ruleIndex) => () => setState(over(
    lensPath(['values', 'pages', sectionIndex, 'widgets', rowIndex, 'rulesBreakPage']),
    remove(ruleIndex, 1)
  ));

  const updateManualBreakPageRule = (sectionIndex) => (rowIndex) => (ruleIndex) => (fn) => setState(over(
    lensPath(['values', 'pages', sectionIndex, 'widgets', rowIndex, 'rulesBreakPage']),
    adjust(ruleIndex, fn)
  ));

  const updatePaddingRow = (sectionIndex, type) => rowIndex => (key, value) => {
    const row = path([...pathWidgetsSection(sectionIndex, type), rowIndex], state);
    const widgetId = firstRowWidgetId(row);
    if (isNotNil(widgetId)) {
      emit(buildEntry({
        action: ACTIONS.COMPOSITION_UPDATE,
        targetType: TARGET_TYPES.WIDGET,
        sectionIndex,
        rowIndex,
        block: type,
        widgetId,
        targetName: getWidgetName(widgetId)
      }));
    }
    return setState(over(
      lensPath(['values', 'pages', sectionIndex, type, rowIndex, 'padding']),
      assoc(key, value)
    ));
  };

  const removeWidget = (sectionIndex, type) => (rowIndex, index) => {
    const row = path(pathWidgetsRow(sectionIndex, rowIndex, type), state) || [];
    const removedWidgetId = prop('widgetId', row[index]) || null;
    if (isNotNil(removedWidgetId)) {
      emit(buildEntry({
        action: ACTIONS.DELETION,
        targetType: TARGET_TYPES.WIDGET,
        sectionIndex,
        rowIndex,
        widgetIndex: index,
        block: type,
        widgetId: removedWidgetId,
        targetName: getWidgetName(removedWidgetId),
        placements: placementsBeforeMutation(removedWidgetId, sectionIndex, type)
      }), { occurrencesAfter: occurrencesAfterRemoval(removedWidgetId, sectionIndex, type) });
    }
    // Removing a row shifts the rowIndex of every row below it (or the
    // widgetIndex of every widget after it, when only the column is removed):
    // either way, entries already on the stack cannot be trusted against the
    // next one on those indexes.
    notifyStructuralChange();
    return ifElse(
      compose(
        lt(1),
        length,
        path(pathWidgetsRow(sectionIndex, rowIndex, type))
      ),
      () => setState(over(
        lensPath(pathWidgetsRow(sectionIndex, rowIndex, type)),
        remove(index, 1)
      )),
      () => {
        const updateFnArray = [over(
          lensPath(pathWidgetsSection(sectionIndex, type)),
          remove(rowIndex, 1)
        )];
        if (equals('widgets', type)) updateFnArray.unshift(updateManualBreakPageState(sectionIndex, rowIndex, false));
        setState(compose(...updateFnArray));
      }
    )(state);
  };

  const moveRowWidget = (sectionIndex, type) => (oldIndex, newIndex) => {
    const widgetOldIndex = path([...pathWidgetsSection(sectionIndex, type), oldIndex], state);
    const widgetNewIndex = path([...pathWidgetsSection(sectionIndex, type), newIndex], state);
    setState(
      over(
        lensPath(pathWidgetsSection(sectionIndex, type)),
        compose(
          update(oldIndex, {
            ...widgetNewIndex, breakPage: prop('breakPage', widgetOldIndex), rulesBreakPage: prop('rulesBreakPage', widgetOldIndex)
          }),
          update(newIndex, {
            ...widgetOldIndex, breakPage: prop('breakPage', widgetNewIndex), rulesBreakPage: prop('rulesBreakPage', widgetNewIndex)
          })
        )
      )
    );
    emit(buildEntry({
      action: ACTIONS.COMPOSITION_UPDATE,
      targetType: TARGET_TYPES.SECTION,
      sectionIndex,
      rowIndex: newIndex,
      block: type,
      widgetId: firstRowWidgetId(widgetOldIndex)
    }));
  };

  // widgetName is an optional override: when a widget is created in a document slot,
  // it is not yet in the `widgets` list captured by this hook's render, so
  // getWidgetName(widgetId) would return null and the ADDITION entry would fail the
  // schema's required targetName (dropping the whole history batch). The caller
  // (useDocument creation effect) passes the fresh name to avoid that.
  const updateWidget = (globalVariablesNames, sectionIndex, type) => (rowIndex, index, widgetId, widgetName) => {
    const rulesLastWidget = path([...pathDataRules(sectionIndex, rowIndex, index, type)], state);
    // Read before setState so both the deletion decision and its placements are
    // computed on the slot as it stands before the change.
    const rawPreviousWidgetId = path([...pathWidgetsRow(sectionIndex, rowIndex, type), index, 'widgetId'], state);
    const previousWidgetId = isNil(rawPreviousWidgetId) ? null : rawPreviousWidgetId;
    const nextWidgetId = isNil(widgetId) ? null : widgetId;
    const widgetIdChanged = notEqual(previousWidgetId, nextWidgetId);
    const outgoingPlacements = widgetIdChanged && isNotNil(previousWidgetId)
      ? placementsBeforeMutation(previousWidgetId, sectionIndex, type)
      : [];

    const newRules = compose(
      reject((rule) => isNilOrEmpty(prop('conditions', rule))),
      map((rule) => {
        const conditionsToKeep = filter((condition) => {
          const hasReusableVariable = includes(prop('variable', condition), globalVariablesNames);
          const isValid = getConditionsAreValid([condition]);
          return hasReusableVariable && isValid;
        })(propOr([], 'conditions', rule));
        return { ...rule, conditions: conditionsToKeep };
      })
    )(rulesLastWidget);

    setState(over(
      lensPath(pathWidgetsRow(sectionIndex, rowIndex, type)),
      adjust(index, compose(
        assoc('type', equals('widgets', type) ? 'content' : type),
        assoc('widgetId', widgetId),
        assoc('rules', newRules)
      ))
    ));
    // Clearing the cascader (the little cross) and replacing an occupied slot both
    // land here. Journal the outgoing widget first so the history reads
    // deletion-then-addition, whatever gesture the user actually made. Re-picking
    // the widget already in place is not a modification and emits nothing.
    if (widgetIdChanged && isNotNil(previousWidgetId)) {
      emit(buildEntry({
        action: ACTIONS.DELETION,
        targetType: TARGET_TYPES.WIDGET,
        rowIndex,
        widgetIndex: index,
        sectionIndex,
        block: type,
        widgetId: previousWidgetId,
        targetName: getWidgetName(previousWidgetId),
        placements: outgoingPlacements
      }), { occurrencesAfter: occurrencesAfterRemoval(previousWidgetId, sectionIndex, type) });
    }

    if (widgetIdChanged && isNotNil(nextWidgetId)) {
      emit(buildEntry({
        action: ACTIONS.ADDITION,
        targetType: TARGET_TYPES.WIDGET,
        rowIndex,
        widgetIndex: index,
        sectionIndex,
        block: type,
        widgetId: nextWidgetId,
        targetName: isNotNil(widgetName) ? widgetName : getWidgetName(nextWidgetId)
      }));
    }
  };

  const updateBreakPage = (sectionIndex, value) => setState(
    assocPath(['values', 'pages', sectionIndex, 'breakPage'], value)
  );

  const addWidgetRule = (sectionIndex, type) => (rowIndex, widgetIndex) => () => {
    const widgetId = path([...pathWidgetsRow(sectionIndex, rowIndex, type), widgetIndex, 'widgetId'], state) || null;
    emit(buildEntry({
      action: ACTIONS.CONDITIONS_UPDATE,
      targetType: TARGET_TYPES.WIDGET,
      rowIndex,
      widgetIndex,
      sectionIndex,
      block: type,
      widgetId,
      targetName: getWidgetName(widgetId)
    }));
    return setState(over(
      lensPath(pathDataRules(sectionIndex, rowIndex, widgetIndex, type)),
      compose(
        append({}),
        defaultTo([])
      )
    ));
  };

  const removeWidgetRule = (sectionIndex, type) => (rowIndex, widgetIndex) => (ruleIndex) => () => {
    const widgetId = path([...pathWidgetsRow(sectionIndex, rowIndex, type), widgetIndex, 'widgetId'], state) || null;
    emit(buildEntry({
      action: ACTIONS.CONDITIONS_UPDATE,
      targetType: TARGET_TYPES.WIDGET,
      rowIndex,
      widgetIndex,
      sectionIndex,
      block: type,
      widgetId,
      targetName: getWidgetName(widgetId)
    }));
    return setState(over(
      lensPath(pathDataRules(sectionIndex, rowIndex, widgetIndex, type)),
      remove(ruleIndex, 1)
    ));
  };

  const updateWidgetRule = (sectionIndex, type) => (rowIndex, widgetIndex) => (ruleIndex) => (fn) => {
    const widgetId = path([...pathWidgetsRow(sectionIndex, rowIndex, type), widgetIndex, 'widgetId'], state) || null;
    emit(buildEntry({
      action: ACTIONS.CONDITIONS_UPDATE,
      targetType: TARGET_TYPES.WIDGET,
      rowIndex,
      widgetIndex,
      sectionIndex,
      block: type,
      widgetId,
      targetName: getWidgetName(widgetId)
    }));
    return setState(over(
      lensPath(pathDataRules(sectionIndex, rowIndex, widgetIndex, type)),
      adjust(ruleIndex, fn)
    ));
  };

  return {
    state,
    widgetsAreDefined,
    addWidget,
    addRowWidget,
    removeWidget,
    updateWidget,
    moveRowWidget,
    updateBreakPage,
    addWidgetRule,
    removeWidgetRule,
    updateWidgetRule,
    updateManualBreakPage,
    updatePaddingRow,
    addManualBreakPageRule,
    removeManualBreakPageRule,
    updateManualBreakPageRule
  };
};
